import React, { useState } from 'react';
import { jobName } from '../../utils/ui';

// The shared Projects table — used on the Projects page (with PM column + delete)
// and on each PM at-a-glance page (without them). Sortable People / Equip columns.

export function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// The Job # to show. Legacy (Trello) projects stored an 8-char hex board id in
// project_number; the real number ("2629", "WO26901") is the start of the name.
export function jobNumber(p) {
  const pn = (p.project_number || '').trim();
  if (pn && !/^[0-9a-f]{8}$/i.test(pn)) return pn;
  const m = (p.name || '').match(/^\s*(WO\s?\d{3,6}|\d{3,6})/i);
  return m ? m[1].replace(/\s+/g, '') : (pn || '—');
}

// Which scheduled board(s) a job is on. A work order (small) is on exactly one
// (its schedule_workflow); a large job can be on several (from its sub-jobs).
const BOARD_META = {
  vertical_drilling:   { label: 'Vert. Drill',  color: '#1e3a5f', bg: '#eef2ff' },
  horizontal_drilling: { label: 'Directional',  color: '#4a1d50', bg: '#f5edf7' },
  loops:               { label: 'Loops',        color: '#059669', bg: '#ecfdf5' },
  small_jobs:          { label: 'Small Jobs',   color: '#7c3aed', bg: '#f5f3ff' },
};
function boardsFor(p) {
  if (p.division === 'small') return [p.schedule_workflow || 'small_jobs'];
  const types = new Set((p.subJobs || []).map(sj => sj.type));
  return ['vertical_drilling', 'horizontal_drilling', 'loops'].filter(t => types.has(t));
}

// The one definition of how a USA locate reads — wording and colours. Exported so
// the drilling pillows use exactly this rather than a second copy that drifts.
export const USA_STATUS = {
  not_called_in: { label: 'Not Called In', bg: '#f3f4f6', color: '#6b7280' },
  called_in:     { label: 'Called In',     bg: '#eff6ff', color: '#1d4ed8' },
  active:        { label: 'Active',        bg: '#dcfce7', color: '#15803d' },
  expiring_soon: { label: 'Expiring Soon', bg: '#fef3c7', color: '#b45309' },
  expired:       { label: 'Expired',       bg: '#fee2e2', color: '#b91c1c' },
};

// The job's own status, as a column: a picker where the page allows changing it
// (onStatusChange given — the office Projects list), a pill everywhere else. Sorts
// active → complete → closed. Start Date came off (Pat, Sep 2026): the status is what
// the list is scanned for; the date lives on the job.
const JOB_STATUS = [['active', 'Active'], ['complete', 'Complete'], ['closed', 'Closed']];
const STATUS_LOOK = { active: { bg: '#eff6ff', color: '#1d4ed8' }, complete: { bg: '#f0fdf4', color: '#15803d' }, closed: { bg: '#f3f4f6', color: '#6b7280' } };
const statusRank = p => Math.max(0, JOB_STATUS.findIndex(([k]) => k === (p.status || 'active')));
export default function ProjectsTable({ rows, onOpen, favIds, onToggleFavorite, usaByProject = {}, liveCounts = {}, showPM = true, pmName = () => '', canDelete = false, onDelete, onStatusChange = null }) {
  const [sortBy, setSortBy] = useState('job');   // 'job' | 'crew' | 'equipment' | 'status'
  const [sortDir, setSortDir] = useState('desc');
  const toggleSort = col => {
    if (sortBy === col) setSortDir(d => (d === 'desc' ? 'asc' : 'desc'));
    else { setSortBy(col); setSortDir('desc'); }
  };
  const crewOf = p => liveCounts[p.id]?.crew || 0;
  const equipOf = p => liveCounts[p.id]?.equipment || 0;
  const crewTip = p => { const n = liveCounts[p.id]?.crewNames || []; return n.length ? 'On the clock:\n• ' + n.join('\n• ') : 'Nobody clocked in'; };
  const equipTip = p => { const n = liveCounts[p.id]?.equipList || []; return n.length ? (crewOf(p) === 0 ? 'Equipment sitting here (nobody clocked in):\n• ' : 'On this job:\n• ') + n.join('\n• ') : 'No equipment on this job'; };

  // Every column sorts (Pat, Sep 25 2026). Text columns compare as text, counts as
  // numbers; ties fall back to the job number so the order never jitters.
  const text = v => String(v || '').trim().toLowerCase();
  const usaRank = p => { const u = usaByProject[p.id]; return u ? (Object.keys(USA_STATUS).indexOf(u.status) + 1) : 0; };
  const sorted = [...rows].sort((a, b) => {
    const jobCmp = () => {
      const na = parseInt((jobNumber(a).match(/\d+/) || [])[0], 10);
      const nb = parseInt((jobNumber(b).match(/\d+/) || [])[0], 10);
      const va = isNaN(na) ? -Infinity : na;
      const vb = isNaN(nb) ? -Infinity : nb;
      return vb - va || jobNumber(b).localeCompare(jobNumber(a));
    };
    // "desc" on a text column means Z → A, on a count column biggest first.
    const byText = (va, vb) => text(vb).localeCompare(text(va)) || jobCmp();
    let cmp;
    if (sortBy === 'crew') cmp = (crewOf(b) - crewOf(a)) || jobCmp();
    else if (sortBy === 'equipment') cmp = (equipOf(b) - equipOf(a)) || jobCmp();
    else if (sortBy === 'status') cmp = (statusRank(b) - statusRank(a)) || jobCmp();
    else if (sortBy === 'name') cmp = byText(jobName(a), jobName(b));
    else if (sortBy === 'owner') cmp = byText(a.client_name, b.client_name);
    else if (sortBy === 'pm') cmp = byText(pmName(a), pmName(b));
    else if (sortBy === 'type') cmp = byText(a.division === 'small' ? 'work order' : 'large job', b.division === 'small' ? 'work order' : 'large job') ;
    else if (sortBy === 'board') cmp = byText(boardsFor(a).join(' '), boardsFor(b).join(' '));
    else if (sortBy === 'usa') cmp = (usaRank(b) - usaRank(a)) || jobCmp();
    else cmp = jobCmp();
    return sortDir === 'asc' ? -cmp : cmp;
  });
  const sortable = (col, label, extra = {}) => (
    <th key={col} onClick={() => toggleSort(col)} title={`Sort by ${label}`}
      style={{ cursor: 'pointer', whiteSpace: 'nowrap', userSelect: 'none', color: sortBy === col ? '#1e3a5f' : undefined, ...extra }}>
      {label}{sortBy === col ? (sortDir === 'desc' ? ' ▼' : ' ▲') : ''}
    </th>
  );

  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="admin-table">
        <thead>
          <tr>
            {favIds && <th style={{ width: 34 }}></th>}
            {sortable('job', 'Job #')}
            {sortable('name', 'Job Name')}
            {sortable('owner', 'Owner')}
            {showPM && sortable('pm', 'PM')}
            {sortable('type', 'Type')}
            {sortable('status', 'Status')}
            {sortable('board', 'Board')}
            {sortable('usa', 'USA Dig')}
            {[['crew', '👷 People'], ['equipment', '🔧 Equip']].map(([col, label]) => (
              <th key={col} onClick={() => toggleSort(col)}
                title={col === 'crew' ? 'People clocked into this job right now (QuickBooks Time)' : 'Construction equipment sitting on this job'}
                style={{ cursor: 'pointer', textAlign: 'center', whiteSpace: 'nowrap', userSelect: 'none', color: sortBy === col ? '#1e3a5f' : undefined }}>
                {label}{sortBy === col ? (sortDir === 'desc' ? ' ▼' : ' ▲') : ''}
              </th>
            ))}
            {canDelete && <th style={{ width: 36 }}></th>}
          </tr>
        </thead>
        <tbody>
          {sorted.map(p => {
            return (
              <tr key={p.id} onClick={() => onOpen(p)} style={{ cursor: 'pointer' }}>
                {favIds && (
                  <td style={{ textAlign: 'center' }}>
                    <button onClick={e => onToggleFavorite(e, p.id)} title={favIds.has(p.id) ? 'Remove from favorites' : 'Add to favorites'}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 17, lineHeight: 1, padding: 2, color: favIds.has(p.id) ? '#f59e0b' : '#d1d5db' }}>
                      {favIds.has(p.id) ? '★' : '☆'}
                    </button>
                  </td>
                )}
                <td><span style={{ fontWeight: 700, color: 'var(--orange)', fontFamily: 'monospace', fontSize: 14 }}>{jobNumber(p)}</span></td>
                <td style={{ maxWidth: 220 }} title={jobName(p)}>
                  <span className="admin-list-primary" style={{ display: 'block', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{jobName(p)}</span>
                </td>
                <td><span className="admin-list-secondary">{p.client_name || '—'}</span></td>
                {showPM && (
                  <td>
                    {pmName(p)
                      ? <span style={{ fontSize: 12, fontWeight: 600, color: '#1e3a5f' }}>{pmName(p)}</span>
                      : <span style={{ fontSize: 11, color: '#dc2626', fontWeight: 600 }}>Unassigned</span>}
                  </td>
                )}
                <td>
                  <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 4,
                    background: p.division === 'small' ? '#eff6ff' : '#fef3c7',
                    color: p.division === 'small' ? '#1d4ed8' : '#b45309' }}>
                    {p.division === 'small' ? 'Work Order' : 'Large Job'}
                  </span>
                </td>
                <td onClick={e => { if (onStatusChange) e.stopPropagation(); }}>
                  {(() => {
                    const st = JOB_STATUS.some(([k]) => k === p.status) ? p.status : 'active';
                    const look = STATUS_LOOK[st];
                    const box = { fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 4, background: look.bg, color: look.color, border: 'none' };
                    return onStatusChange
                      ? <select value={st} onChange={e => onStatusChange(p, e.target.value)} title="Change this job's status" style={{ ...box, cursor: 'pointer' }}>
                          {JOB_STATUS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                        </select>
                      : <span style={box}>{(JOB_STATUS.find(([k]) => k === st) || [])[1]}</span>;
                  })()}
                </td>
                <td>
                  {(() => {
                    const boards = boardsFor(p);
                    if (!boards.length) return <span style={{ color: '#d1d5db', fontSize: 12 }}>—</span>;
                    return (
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {boards.map(b => { const m = BOARD_META[b] || { label: b, color: '#374151', bg: '#f3f4f6' }; return (
                          <span key={b} style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 10, background: m.bg, color: m.color, whiteSpace: 'nowrap' }}>{m.label}</span>
                        ); })}
                      </div>
                    );
                  })()}
                </td>
                <td>
                  {(() => {
                    const u = usaByProject[p.id];
                    const meta = u && USA_STATUS[u.status];
                    if (!meta) return <span style={{ color: '#d1d5db', fontSize: 12 }}>—</span>;
                    return (
                      <span title={`${u.count} USA ticket${u.count !== 1 ? 's' : ''}`}
                        style={{ fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 4, background: meta.bg, color: meta.color, whiteSpace: 'nowrap' }}>
                        {meta.label}{u.count > 1 ? ` · ${u.count}` : ''}
                      </span>
                    );
                  })()}
                </td>
                <td style={{ textAlign: 'center' }} title={crewTip(p)}>
                  {crewOf(p) > 0
                    ? <span style={{ fontSize: 12, fontWeight: 800, color: '#15803d', background: '#dcfce7', padding: '3px 9px', borderRadius: 999, cursor: 'help' }}>{crewOf(p)}</span>
                    : <span style={{ color: '#d1d5db', fontSize: 13 }}>—</span>}
                </td>
                <td style={{ textAlign: 'center' }} title={equipTip(p)}>
                  {equipOf(p) > 0
                    ? <span style={{ fontSize: 12, fontWeight: 800, cursor: 'help', color: crewOf(p) === 0 ? '#b45309' : '#1e3a5f', background: crewOf(p) === 0 ? '#fef3c7' : '#eff6ff', padding: '3px 9px', borderRadius: 999 }}>{equipOf(p)}</span>
                    : <span style={{ color: '#d1d5db', fontSize: 13 }}>—</span>}
                </td>
                {canDelete && (
                  <td onClick={e => onDelete(e, p)} title="Delete job"
                    style={{ textAlign: 'center', padding: '0 8px', cursor: 'pointer', color: '#d1d5db' }}
                    onMouseEnter={e => e.currentTarget.style.color = '#dc2626'}
                    onMouseLeave={e => e.currentTarget.style.color = '#d1d5db'}>
                    <svg width="15" height="15" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.4">
                      <path d="M2 3.5h10M5.5 3.5V2.5a1 1 0 011-1h1a1 1 0 011 1v1M3 3.5l.5 8a1 1 0 001 1h5a1 1 0 001-1l.5-8" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
